<template>
  <ul class="toplist">
    <li
      v-for="(row, index) in rows"
      :key="row[0]"
      class="toplist-item"
      @mouseenter="() => onMouseEnter(row[0])"
      @mouseleave="removeLastHoveredItem"
    >
      <div class="item-label">
        <TextBase
          :text="getEntityName(row[0])"
          :weight="'bold'"
          :type="'label'"
          :style="{ 'line-height': 'unset' }"
        />
        <TextBase
          :text="''"
          :weight="'bold'"
          :type="'label'"
          :color="'#767676'"
          :style="{ 'line-height': 'unset' }"
        />
      </div>
      <div class="item-chart">
        <ChartBase
          height="16px"
          :style="{
            width: '100%',
            height: '16px',
            color: '#282828'
          }"
          :paddingOverride="{ top: 20, bottom: 20, left: 0, right: 0 }"
          :components="rowComponents[index]"
        />
      </div>
    </li>
  </ul>
</template>
<script lang="ts" setup>
import { computed } from 'vue'
import { Limits } from './ChartComponent/chart/limits'
import { FormatOptions } from '../formatter'
import TextBase from '@/design-system/atoms/TextBase.vue'
import { ToplistResponse } from '@/api-client/models/indoorclimate/Contracts'
import { Bar, Pattern, createBars } from './ChartComponent/chart/bars'
import { AreaHoverHighlight, createAreaHightlights } from './ChartComponent/chart/area-highlight'
import { createTooltip } from './ChartComponent/chart/tooltip'
import { ChartComponent } from './ChartComponent'
import ChartBase from './ChartComponent/ChartBase.vue'
import { StringLocale } from 'yup/lib/locale'
import { useI18n } from 'vue-i18n'
import { useHoverStore } from '@/pinia/hover'
import { useEntityLookup } from '@/composables/useEntityLookup'
import { capitalizeFirstLetter } from '@/utils/capitalizeFirstLetter'

type Props = {
  toplist: ToplistResponse
  format: FormatOptions
  locale: string
  barOptions: BarConfiguration[]
}

const props = defineProps<Props>()

function reverseNormalize(value: number, min: number, max: number): number {
  return value * (max - min) + min
}

const { addHoveredItem, removeLastHoveredItem } = useHoverStore()
const { findEntity, getEntityDisplayString } = useEntityLookup()

function onMouseEnter(id: string) {
  const entity = findEntity(id)
  if (entity) {
    addHoveredItem(entity.id, { color: '#fadb14' })
  }
}

function getEntityName(id: string) {
  const title = getEntityDisplayString(id, true)
  return title ? capitalizeFirstLetter(title) : id
}

const limits: Limits = {
  x: [0, 1],
  y: [0, 1]
}

const rows = computed(() => {
  if (!props.toplist) return []
  const temp = props.toplist.axis.x.map((id, i) => {
    return [id, props.toplist.values[i]] as [string, number[]]
  })
  return temp
})

export type BarConfiguration = {
  label: StringLocale
  color: string
  gradient?: boolean
  pattern?: Pattern
  tooltip?: boolean
}

const rowComponents = computed<ChartComponent[][]>(() => {
  const locale = useI18n().locale.value
  const values: number[][] = rows.value.map((row) => row[1])

  const bars: Bar[][] = rows.value.map((row) => {
    const temp = row[1].map((v, i) => {
      const bar: Bar = {
        ...props.barOptions[i],
        x: 0.5,
        height: v,
        label: props.barOptions[i]?.label[locale]
      }
      return bar
    })
    return temp
  })

  const makeHighlights = (values: number[]) => {
    let temp: AreaHoverHighlight[] = []
    let i = 0
    for (let value of values) {
      const accumulated = values.slice(0, i + 1).reduce((a, b) => a + b, 0)
      const curr = values.slice(0, i).reduce((a, b) => a + b, 0)
      if (props.barOptions[i] && props.barOptions[i].tooltip === false) {
        i++
        continue
      }
      const highlight: AreaHoverHighlight = {
        label: bars[0][i].label,
        color: bars[0][i].color,
        pattern: bars[0][i].pattern,
        path: [
          [curr, 0],
          [accumulated, 0],
          [accumulated, 1],
          [curr, 1]
        ],
        value: [0, reverseNormalize(value, props.toplist.min, props.toplist.max)],
        visible: false
      }
      temp.push(highlight)
      i++
    }
    return temp
  }

  const barComponents = bars.map((b, i) => [
    ...createTooltip(limits, props.format),
    ...createBars([b], 1, 'horizontal', limits),
    ...createAreaHightlights(makeHighlights(values[i]), limits)
  ])
  return barComponents
})
</script>

<style lang="scss" scoped>
.toplist {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 16px;
  gap: 16px;
}

.toplist-item {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
}

.item-label {
  display: flex;
  flex-direction: row;
  gap: 5px;
  width: fit-content;
  border-bottom: 1px solid transparent;

  &:hover {
    cursor: pointer;
    border-bottom-color: currentColor;
  }
}
</style>
